/**
 * Auto theme — time-of-day light/dark selection.
 *
 * Pure logic + storage helpers. The provider in `theme-provider.tsx`
 * binds these to the React tree; the pre-paint script in `layout.tsx`
 * calls `pickInitialMode()` synchronously before hydration to avoid a
 * light-then-dark flash.
 *
 * Resolution order (highest → lowest priority):
 *   1. `?face=light|dark` on the URL — forces the face for THIS page load
 *      only, never persisted. The design gallery is captured this way; a
 *      headless browser has no localStorage and the clock picks the wrong
 *      face half the day. It lives here, in the module that owns the face,
 *      so the pre-paint script, the provider's mount sync and its minute
 *      tick all agree — a page-level effect toggling the class would be
 *      overwritten by whichever of those ran last.
 *   2. User override stored in localStorage as "theme" = "light" | "dark"
 *   3. Time-of-day rule (dark between DARK_FROM and DARK_TO, otherwise light)
 *
 * Why time-of-day and not `prefers-color-scheme`? OS dark mode is set
 * once and forgotten. A workspace that's used both early-morning and
 * late-evening should follow daylight automatically. The user override
 * lane preserves "I want dark always" without monkey-patching.
 *
 * Defaults are conservative — dark from 19:00 to 06:00 local time.
 */

export type Mode = "light" | "dark";
export type Preference = Mode | "auto";

export const STORAGE_KEY = "theme";

// Dark from 19:00 → 06:00 (inclusive of 19, exclusive of 06).
// Tunable via DARK_FROM / DARK_TO; kept as integers so the comparison
// is total-ordered without DST landmines.
export const DARK_FROM_HOUR = 19;
export const DARK_TO_HOUR = 6;

export function modeForHour(hour: number): Mode {
  // dark when hour >= 19 OR hour < 6
  return hour >= DARK_FROM_HOUR || hour < DARK_TO_HOUR ? "dark" : "light";
}

export function modeForDate(d: Date): Mode {
  return modeForHour(d.getHours());
}

/**
 * Read the user override. Returns null when "auto" (or invalid).
 * Safe to call on the server — returns null when window is unavailable.
 */
export function readOverride(): Mode | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" ? v : null;
}

export function writePreference(pref: Preference): void {
  if (typeof window === "undefined") return;
  if (pref === "auto") {
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    window.localStorage.setItem(STORAGE_KEY, pref);
  }
}

/**
 * The per-load force, if the URL carries one. Safe on the server — null.
 */
export function readForcedFace(): Mode | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("face");
  return v === "light" || v === "dark" ? v : null;
}

/**
 * The mode that should be active right now: URL force, then stored
 * override, then the clock. The pre-paint script and the provider both
 * implement exactly this order.
 */
export function resolveMode(now: Date = new Date()): Mode {
  return readForcedFace() ?? readOverride() ?? modeForDate(now);
}

/**
 * Inline script body — runs synchronously in <head> before hydration.
 * Setting the class on <html> here means the first paint already has
 * the correct background and there's no flash.
 *
 * Stringified and dangerouslySetInnerHTML'd from layout.tsx. Must NOT
 * reference any module-level symbol — it executes in the document, not
 * the bundle.
 */
export const PRE_PAINT_SCRIPT = `
(function(){try{
  var k="${STORAGE_KEY}",v=null,f=null;
  try{f=new URLSearchParams(location.search).get("face");}catch(e){}
  try{v=localStorage.getItem(k);}catch(e){}
  var mode;
  if(f==="light"||f==="dark"){mode=f;}
  else if(v==="light"||v==="dark"){mode=v;}
  else{var h=new Date().getHours();mode=(h>=${DARK_FROM_HOUR}||h<${DARK_TO_HOUR})?"dark":"light";}
  var root=document.documentElement;
  if(mode==="dark")root.classList.add("dark");else root.classList.remove("dark");
}catch(e){}})();
`.trim();
